import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useFlipperFlag } from './useFlipperFlag'
import { useMemo } from 'react'
import { useCurrentSchedule } from './useCurrentSchedule'

export const useNeedsCreditsToGenerateContent = (
  socialCount?: number,
  blogAndEmailCount?: number,
  shortFormVideoCount?: number,
) => {
  const {
    socialCount: scheduleSocialCount,
    blogAndEmailCount: scheduleBlogAndEmailCount,
    shortFormVideoCount: scheduleShortFormVideoCount,
  } = useCurrentSchedule()
  const isPioneerCreditsEnabled = useFlipperFlag('credits_styles_videos')
  const profile = useBrandProfile({ cacheTime: 5 * 60 * 1000 })
  const hasAnimatedImage = !!profile?.data?.brand_kit?.content_preferences?.video_sources?.includes('animated_image')
  const hasStylizedImage = !!profile?.data?.brand_kit?.content_preferences?.apply_visual_style
  const hasMultipleFeaturedMedia = (profile?.data?.brand_kit?.content_preferences?.featured_media?.length || 0) > 1
  const hasFeaturedImage = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('image')
  const hasFeaturedVideo = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('video')
  const {
    creditUsageStore: { creditUsage },
    tierUsageStore: { currentSubscription, autopilotAddOns },
  } = useRootStore()
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isTrialing = currentSubscription?.status === 'trialing'
  const usageCosts = creditUsage?.usage_costs
  const contentGenerationCost = isOnPioneer ? 0 : usageCosts?.content_credits_cost || 0
  const visualStylesCost = usageCosts?.visual_styles_credits_cost || 0
  const animatedImagesCost = usageCosts?.video_credits_cost || 0

  const { needsCredits, creditsNeeded, creditsRemaining } = useMemo(() => {
    if (!creditUsage || Object.keys(creditUsage).length === 0) {
      return { needsCredits: false, creditsNeeded: 0, creditsRemaining: 0 }
    }

    // Use provided counts if available, otherwise fall back to current schedule counts
    const effectiveSocialCount = socialCount ?? scheduleSocialCount
    const effectiveBlogAndEmailCount = blogAndEmailCount ?? scheduleBlogAndEmailCount
    const effectiveShortFormVideoCount = shortFormVideoCount ?? scheduleShortFormVideoCount

    let socialCountCreditsNeeded = 0
    const blogAndEmailCountCreditsNeeded = effectiveBlogAndEmailCount * contentGenerationCost
    let shortFormVideoCountCreditsNeeded = 0

    // socialCounts cost calculation
    if (hasMultipleFeaturedMedia) {
      if (hasFeaturedImage) {
        if (hasStylizedImage) {
          socialCountCreditsNeeded += Math.round(effectiveSocialCount * 0.5) * visualStylesCost
        } else {
          socialCountCreditsNeeded += Math.round(effectiveSocialCount * 0.5) * contentGenerationCost
        }
      }
      if (hasFeaturedVideo) {
        if (hasAnimatedImage) {
          socialCountCreditsNeeded += Math.round(effectiveSocialCount * 0.5) * animatedImagesCost
          if (hasStylizedImage) {
            shortFormVideoCountCreditsNeeded += Math.round(effectiveShortFormVideoCount * 0.5) * visualStylesCost
          }
        } else {
          socialCountCreditsNeeded += Math.round(effectiveSocialCount * 0.5) * contentGenerationCost
        }
      }
    } else if (hasFeaturedImage && hasStylizedImage) {
      socialCountCreditsNeeded = effectiveSocialCount * visualStylesCost
    } else if (hasFeaturedVideo && hasAnimatedImage) {
      socialCountCreditsNeeded = effectiveSocialCount * animatedImagesCost
    } else {
      socialCountCreditsNeeded = effectiveSocialCount * contentGenerationCost
    }

    // Short form video cost calculation
    if (hasAnimatedImage) {
      shortFormVideoCountCreditsNeeded = Math.round(effectiveShortFormVideoCount * 0.5) * animatedImagesCost

      if (hasStylizedImage) {
        shortFormVideoCountCreditsNeeded += Math.round(effectiveShortFormVideoCount * 0.5) * visualStylesCost
      }
      shortFormVideoCountCreditsNeeded += Math.round(effectiveShortFormVideoCount * 0.5) * contentGenerationCost
    } else {
      shortFormVideoCountCreditsNeeded = effectiveShortFormVideoCount * contentGenerationCost
    }

    const creditsNeeded = socialCountCreditsNeeded + blogAndEmailCountCreditsNeeded + shortFormVideoCountCreditsNeeded
    const creditsRemaining = creditUsage.credits_remaining || 0

    let needsCredits = creditsNeeded > 0 && creditsNeeded > creditsRemaining

    if (isOnPioneer) {
      needsCredits = isPioneerCreditsEnabled && needsCredits && !isTrialing && (hasAnimatedImage || hasStylizedImage)
    }

    return { needsCredits, creditsNeeded, creditsRemaining }
  }, [
    socialCount,
    blogAndEmailCount,
    shortFormVideoCount,
    scheduleSocialCount,
    scheduleBlogAndEmailCount,
    scheduleShortFormVideoCount,
    creditUsage,
    isPioneerCreditsEnabled,
    isOnPioneer,
    isTrialing,
    hasAnimatedImage,
    hasStylizedImage,
    animatedImagesCost,
    contentGenerationCost,
    hasFeaturedImage,
    hasFeaturedVideo,
    hasMultipleFeaturedMedia,
    visualStylesCost,
  ])

  return {
    needsCredits,
    creditsNeeded,
    creditsRemaining,
    autopilotContentCreditsPrice,
  }
}
